import React, { createContext, useContext, useState, ReactNode } from "react";
import ConfirmAlert from "../ConfirmAlert";

interface AlertOptions {
  title?: string;
  message: string;
  type?: "success" | "error" | "warning" | "info";
  confirmText?: string;
  cancelText?: string;
  showCancel?: boolean;
  onConfirm?: () => void;
  handleCancel?: () => void; // Optional
}

interface AlertContextType {
  showConfirm: (options: AlertOptions) => void;
  hideConfirm: () => void;
}

const AlertContext = createContext<AlertContextType | undefined>(undefined);

export const useAlert = () => {
  const context = useContext(AlertContext);
  if (!context) throw new Error("useAlert must be used within AlertProvider");
  return context;
};

export const AlertProvider = ({ children }: { children: ReactNode }) => {
  const [open, setOpen] = useState(false);
  const [alertData, setAlertData] = useState<AlertOptions | null>(null);

  const showConfirm = (options: AlertOptions) => {
    setAlertData(options);
    setOpen(true);
  };

  const hideConfirm = () => {
    setOpen(false);
    setAlertData(null);
  };

  const handleConfirm = () => {
    alertData?.onConfirm?.();
    hideConfirm();
  };

  const handleCancel = () => {
    alertData?.handleCancel?.(); // Optional callback
    hideConfirm();
  };

  return (
    <AlertContext.Provider value={{ showConfirm, hideConfirm }}>
      {children}

      <ConfirmAlert
        open={open}
        title={alertData?.title}
        message={alertData?.message || ""}
        type={alertData?.type}
        confirmText={alertData?.confirmText}
        cancelText={alertData?.cancelText}
        showCancel={alertData?.showCancel}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </AlertContext.Provider>
  );
};